import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Separator } from "../ui/separator";
import { Switch } from "../ui/switch";
import { CreditCard, Save, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApi, currencies } from "./useApi";

export function SubscriptionSettingsCard({ token, t }) {
  var [subscriptionSettings, setSubscriptionSettings] = useState({ monthly_price: 29.99, trial_days: 14, grace_period_days: 5, currency: "eur" });
  var [savingSubscription, setSavingSubscription] = useState(false);
  var { fetchJson, putJson } = useApi(token);

  useEffect(function() {
    fetchJson("/api/settings/subscription").then(setSubscriptionSettings).catch(function() {});
  }, []);

  async function handleSaveSubscriptionSettings() {
    setSavingSubscription(true);
    try {
      var response = await putJson("/api/settings/subscription", subscriptionSettings);
      if (response.ok) toast.success(t("settings.subscriptionUpdated"));
      else { var error = await response.json(); toast.error(error.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
    finally { setSavingSubscription(false); }
  }

  return (
    <Card className="bento-card border-accent/50" data-testid="subscription-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-accent" />{t("settings.subscriptionSettings")}</CardTitle>
        <CardDescription>{t("settings.subscriptionSettingsDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="form-label">{t("settings.monthlyPrice")}</Label>
            <div className="flex gap-2">
              <Input type="number" step="0.01" min="0" value={subscriptionSettings.monthly_price} onChange={function(e) { setSubscriptionSettings({ ...subscriptionSettings, monthly_price: parseFloat(e.target.value) || 0 }); }} data-testid="subscription-price-input" />
              <Select value={subscriptionSettings.currency} onValueChange={function(v) { setSubscriptionSettings({ ...subscriptionSettings, currency: v }); }}>
                <SelectTrigger className="w-32" data-testid="subscription-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{currencies.map(function(curr) { return <SelectItem key={curr.code} value={curr.code}>{curr.name}</SelectItem>; })}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="form-label">{t("settings.trialDays")}</Label>
            <Input type="number" min="0" value={subscriptionSettings.trial_days} onChange={function(e) { setSubscriptionSettings({ ...subscriptionSettings, trial_days: parseInt(e.target.value) || 0 }); }} data-testid="subscription-trial-input" />
            <p className="text-xs text-muted-foreground">{t("settings.trialDaysDesc")}</p>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="form-label">{t("settings.gracePeriod")}</Label>
          <Input type="number" min="0" className="max-w-xs" value={subscriptionSettings.grace_period_days} onChange={function(e) { setSubscriptionSettings({ ...subscriptionSettings, grace_period_days: parseInt(e.target.value) || 0 }); }} data-testid="subscription-grace-input" />
          <p className="text-xs text-muted-foreground">{t("settings.gracePeriodDesc")}</p>
        </div>
        <Button onClick={handleSaveSubscriptionSettings} disabled={savingSubscription} data-testid="save-subscription-btn">
          <Save className="w-4 h-4 mr-2" />{savingSubscription ? t("settings.saving") : t("settings.saveSubscriptionSettings")}
        </Button>
      </CardContent>
    </Card>
  );
}

export function PayPalSettingsCard({ token, t }) {
  var [paypalSettings, setPaypalSettings] = useState({ enabled: false, sandbox_mode: true, client_id: "", client_secret: "", has_client_id: false, has_client_secret: false });
  var [savingPaypal, setSavingPaypal] = useState(false);
  var [testingPaypal, setTestingPaypal] = useState(false);
  var [paypalSubscriptionInfo, setPaypalSubscriptionInfo] = useState({ product_configured: false, plan_configured: false });
  var [creatingProduct, setCreatingProduct] = useState(false);
  var [creatingPlan, setCreatingPlan] = useState(false);
  var { fetchApi, fetchJson, putJson, postJson } = useApi(token);

  useEffect(function() {
    fetchPaypalSettings();
    fetchPaypalSubscriptionInfo();
  }, []);

  async function fetchPaypalSettings() {
    try {
      var data = await fetchJson("/api/admin/settings/paypal");
      setPaypalSettings({ enabled: data.enabled || false, sandbox_mode: data.sandbox_mode !== false, client_id: "", client_secret: "", has_client_id: data.has_client_id || false, has_client_secret: data.has_client_secret || false });
    } catch (err) {}
  }

  async function fetchPaypalSubscriptionInfo() {
    try { var data = await fetchJson("/api/admin/paypal/subscription-info"); setPaypalSubscriptionInfo(data); } catch (err) {}
  }

  async function handleSavePaypalSettings() {
    setSavingPaypal(true);
    try {
      var response = await putJson("/api/admin/settings/paypal", { enabled: paypalSettings.enabled, sandbox_mode: paypalSettings.sandbox_mode, client_id: paypalSettings.client_id || undefined, client_secret: paypalSettings.client_secret || undefined });
      if (response.ok) { toast.success(t("settings.paypalUpdated")); fetchPaypalSettings(); setPaypalSettings(function(prev) { return { ...prev, client_id: "", client_secret: "" }; }); }
      else { var error = await response.json(); toast.error(error.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
    finally { setSavingPaypal(false); }
  }

  async function handleTestPaypal() {
    setTestingPaypal(true);
    try {
      var response = await postJson("/api/admin/settings/paypal/test", {});
      if (response.ok) toast.success(t("settings.paypalTestSuccess"));
      else { var error = await response.json(); toast.error(error.detail || t("settings.paypalTestFailed")); }
    } catch (err) { toast.error(t("settings.paypalTestFailed")); }
    finally { setTestingPaypal(false); }
  }

  async function handleCreatePaypalProduct() {
    setCreatingProduct(true);
    try {
      var response = await postJson("/api/admin/paypal/create-product", { name: "Property Central Subscription", description: "Monthly subscription for Property Central real estate management" });
      if (response.ok) { toast.success(t("settings.paypalProductCreated")); fetchPaypalSubscriptionInfo(); }
      else { var error = await response.json(); toast.error(error.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
    finally { setCreatingProduct(false); }
  }

  async function handleCreatePaypalPlan() {
    setCreatingPlan(true);
    try {
      var response = await postJson("/api/admin/paypal/create-plan", { name: "Monthly Subscription", description: "Monthly subscription with automatic renewal" });
      if (response.ok) { toast.success(t("settings.paypalPlanCreated")); fetchPaypalSubscriptionInfo(); }
      else { var error = await response.json(); toast.error(error.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
    finally { setCreatingPlan(false); }
  }

  return (
    <Card className="bento-card border-yellow-500/50" data-testid="paypal-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-yellow-500" />{t("settings.paypalSettings")}</CardTitle>
        <CardDescription>{t("settings.paypalSettingsDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
          <div><p className="font-medium">{t("settings.enablePaypal")}</p><p className="text-sm text-muted-foreground">{t("settings.enablePaypalDesc")}</p></div>
          <Switch checked={paypalSettings.enabled} onCheckedChange={function(v) { setPaypalSettings({ ...paypalSettings, enabled: v }); }} data-testid="paypal-enabled-switch" />
        </div>
        <div className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <div><p className="font-medium">{t("settings.sandboxMode")}</p><p className="text-sm text-muted-foreground">{t("settings.sandboxModeDesc")}</p></div>
          <Switch checked={paypalSettings.sandbox_mode} onCheckedChange={function(v) { setPaypalSettings({ ...paypalSettings, sandbox_mode: v }); }} data-testid="paypal-sandbox-switch" />
        </div>
        <Separator />
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="form-label">{t("settings.paypalClientId")}</Label>
            <Input type="password" value={paypalSettings.client_id} onChange={function(e) { setPaypalSettings({ ...paypalSettings, client_id: e.target.value }); }} placeholder={paypalSettings.has_client_id ? t("settings.clientIdSet") : t("settings.enterClientId")} data-testid="paypal-client-id-input" />
          </div>
          <div className="space-y-2">
            <Label className="form-label">{t("settings.paypalClientSecret")}</Label>
            <Input type="password" value={paypalSettings.client_secret} onChange={function(e) { setPaypalSettings({ ...paypalSettings, client_secret: e.target.value }); }} placeholder={paypalSettings.has_client_secret ? t("settings.clientSecretSet") : t("settings.enterClientSecret")} data-testid="paypal-client-secret-input" />
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={handleSavePaypalSettings} disabled={savingPaypal} data-testid="save-paypal-btn">
            {savingPaypal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {savingPaypal ? t("settings.saving") : t("settings.savePaypalSettings")}
          </Button>
          <Button variant="outline" onClick={handleTestPaypal} disabled={testingPaypal || (!paypalSettings.has_client_id && !paypalSettings.client_id)} data-testid="test-paypal-btn">
            {testingPaypal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            {testingPaypal ? t("settings.testing") : t("settings.testPaypalConnection")}
          </Button>
        </div>
        <div className="p-3 bg-muted/30 rounded-lg text-sm">
          <p className="font-medium mb-1">{t("settings.paypalCredentialsHelp")}</p>
          <p className="text-muted-foreground">{t("settings.paypalCredentialsHelpDesc")}</p>
          <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline mt-1 inline-block">{t("settings.paypalDeveloperDashboard")} &rarr;</a>
        </div>
        <Separator />
        <div className="space-y-4">
          <h4 className="font-medium flex items-center gap-2"><CreditCard className="w-4 h-4" />{t("settings.recurringSubscriptions")}</h4>
          <p className="text-sm text-muted-foreground">{t("settings.recurringSubscriptionsDesc")}</p>
          <div className={"p-4 rounded-lg border " + (paypalSubscriptionInfo.product_configured ? "border-green-500 bg-green-50 dark:bg-green-900/20" : "border-dashed border-muted-foreground/50")}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium flex items-center gap-2">
                  {paypalSubscriptionInfo.product_configured ? <Check className="w-4 h-4 text-green-500" /> : <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs">1</span>}
                  {t("settings.step1CreateProduct")}
                </p>
                {paypalSubscriptionInfo.product_configured ? <p className="text-sm text-muted-foreground mt-1">{t("settings.productId")}: {paypalSubscriptionInfo.product_id}</p> : <p className="text-sm text-muted-foreground mt-1">{t("settings.step1Desc")}</p>}
              </div>
              {!paypalSubscriptionInfo.product_configured && <Button size="sm" onClick={handleCreatePaypalProduct} disabled={creatingProduct || !paypalSettings.has_client_id} data-testid="create-paypal-product-btn">{creatingProduct ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{t("settings.createProduct")}</Button>}
            </div>
          </div>
          <div className={"p-4 rounded-lg border " + (paypalSubscriptionInfo.plan_configured ? "border-green-500 bg-green-50 dark:bg-green-900/20" : "border-dashed border-muted-foreground/50")}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium flex items-center gap-2">
                  {paypalSubscriptionInfo.plan_configured ? <Check className="w-4 h-4 text-green-500" /> : <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs">2</span>}
                  {t("settings.step2CreatePlan")}
                </p>
                {paypalSubscriptionInfo.plan_configured ? <p className="text-sm text-muted-foreground mt-1">{t("settings.planId")}: {paypalSubscriptionInfo.plan_id} | {paypalSubscriptionInfo.plan_currency} {paypalSubscriptionInfo.plan_price}/{t("settings.month")}</p> : <p className="text-sm text-muted-foreground mt-1">{t("settings.step2Desc")}</p>}
              </div>
              {!paypalSubscriptionInfo.plan_configured && <Button size="sm" onClick={handleCreatePaypalPlan} disabled={creatingPlan || !paypalSubscriptionInfo.product_configured} data-testid="create-paypal-plan-btn">{creatingPlan ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{t("settings.createPlan")}</Button>}
            </div>
          </div>
          {paypalSubscriptionInfo.plan_configured && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <p className="font-medium text-green-800 dark:text-green-300 flex items-center gap-2"><Check className="w-5 h-5" />{t("settings.recurringPaymentsReady")}</p>
              <p className="text-sm text-green-700 dark:text-green-400 mt-1">{t("settings.recurringPaymentsReadyDesc")}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
